import { Alert, Button, Card, Flex, Stack, Table, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import type { IntakeAction, IntakeAnswers } from '../types';
import { THERAPY_TYPE_OPTIONS } from '../therapyTypeOptions';
import { type IntakeServiceCatalogEntry, findIntakeServiceForTherapyType } from '../intakeServicesCatalog';

interface StepCostEstimatesAllProps {
  answers: IntakeAnswers;
  dispatch: React.Dispatch<IntakeAction>;
  /** Active service catalog (with per-service standard session rate) for live cash pricing. */
  services?: IntakeServiceCatalogEntry[];
}

const COPAY_RANGE = '$25 – $35';

function formatRate(cost: number | null | undefined): string | null {
  if (cost == null) return null;
  if (cost <= 0) return 'Free';
  return `$${Number.isInteger(cost) ? cost : cost.toFixed(2)}`;
}

export function StepCostEstimatesAll({ answers, dispatch, services }: StepCostEstimatesAllProps): JSX.Element {
  const company = answers.insuranceCompany ?? 'your insurance';
  const isInsurance = answers.payType === 'insurance';
  const therapyTypes = answers.therapyTypes;
  const cancelledSet = new Set(answers.cancelledTherapyTypes ?? []);

  const standardRateFor = (therapyType: string): string | null => {
    const entry = findIntakeServiceForTherapyType(services ?? [], therapyType);
    return formatRate(entry?.sessions.standard?.cost);
  };

  return (
    <Stack gap="lg">
      <Text size="sm" c="dimmed">
        {isInsurance
          ? therapyTypes.length > 1
            ? `We've checked your coverage with ${company}. Here are your cost estimates per service.`
            : `We've checked your coverage with ${company}. Here's your cost estimate.`
          : therapyTypes.length > 1
            ? 'Here are your costs per service.'
            : 'Here\'s your cost.'}
      </Text>

      {therapyTypes.map((therapyType) => {
        const option = THERAPY_TYPE_OPTIONS.find((o) => o.value === therapyType);
        const label = option?.label ?? therapyType;
        const cancelled = cancelledSet.has(therapyType);

        return (
          <Card
            key={therapyType}
            withBorder
            radius="md"
            padding="md"
            style={cancelled ? { opacity: 0.75 } : undefined}
          >
            <Flex justify="space-between" align="center" mb="xs">
              <Text fw={600} size="sm">
                {isInsurance
                  ? `Here's your cost estimate for ${label.toLowerCase()}.`
                  : `Here's your cost for ${label.toLowerCase()}.`}
              </Text>
              {cancelled && (
                <Text size="xs" c="red" fw={600}>
                  Cancelled
                </Text>
              )}
            </Flex>
            {!cancelled && (
              <Table verticalSpacing="xs" withRowBorders={false} highlightOnHover={false}>
                <Table.Tbody>
                  <Table.Tr>
                    <Table.Th style={{ whiteSpace: 'nowrap' }}>{isInsurance ? 'Estimated cost' : 'Cost'}</Table.Th>
                    <Table.Td>
                      {isInsurance
                        ? `${COPAY_RANGE} copay per session`
                        : (() => {
                            const rate = standardRateFor(therapyType);
                            return rate ? `${rate} per session` : 'Cost provided at booking';
                          })()}
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            )}
            <Button
              variant="subtle"
              color="gray"
              size="xs"
              mt="sm"
              onClick={() =>
                dispatch({
                  type: 'SET_THERAPY_TYPE_CANCELLED',
                  payload: { therapyType, cancelled: !cancelled },
                })
              }
            >
              {cancelled ? 'Restore this service' : 'Cancel this service'}
            </Button>
          </Card>
        );
      })}

      {isInsurance && (
        <Alert icon={<IconInfoCircle size={18} />} color="yellow" variant="light" radius="md">
          <Text size="xs">
            These are estimates based on typical in-network rates. We recommend contacting {company}{' '}
            directly to verify your exact out-of-pocket cost. Your actual cost may vary.
          </Text>
        </Alert>
      )}
    </Stack>
  );
}
